import { DeleteExtra, InitialServiceOrderExtra, ServiceOrder, ServiceOrderExtra } from '@services';
import { useStore } from '@stores';
import { Form } from 'antd';
import React, { useCallback, useState } from 'react';
import Presentation from './ServiceOrderExtraTable.presentation';
import { useTenantVat } from '@hooks';
import { observer } from 'mobx-react';

interface IProps {
  hideAction?: boolean;
  serviceOrderExtras: ServiceOrderExtra[];
  serviceOrders: ServiceOrder[];
  disableServiceAdd?: boolean;
  handleServiceOrdersUpdate?: (serviceOrders: ServiceOrder) => void;
}

export const ExtraLabourTable: React.FC<IProps> = observer(
  ({
    serviceOrderExtras,
    serviceOrders = [],
    hideAction = false,
    disableServiceAdd = false,
    handleServiceOrdersUpdate,
  }) => {
    const { orderStore, serviceStore } = useStore();
    const { selectServices } = serviceStore;
    const [tableForm] = Form.useForm();
    const [addForm] = Form.useForm();
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingKey, setEditingKey] = useState('');
    const [deleteExtra, setDeleteExtra] = useState<DeleteExtra>({ id: '', serviceOrderId: '' });
    const isEditing = (record: ServiceOrderExtra) => record?.id === editingKey;

    const tenantVat = useTenantVat();

    const handleCreate = useCallback(
      async (values: any) => {
        const resetFormAndCloseModal = () => {
          setShowAddModal(false);
          addForm.resetFields();
        };

        const updateServiceOrder = (updatedOrder: any) => {
          if (updatedOrder && handleServiceOrdersUpdate) {
            handleServiceOrdersUpdate(updatedOrder);
          }
        };

        const isService = addForm.getFieldValue('type') === 'Service';

        if (isService) {
          const selectedService = selectServices.get(values.serviceId);
          const vat = selectedService?.isVat ? tenantVat : 0;

          const result = await orderStore.createServiceOrder({
            ...values,
            vat,
            extras: [],
            orderId: serviceOrders[0]?.orderId,
          });

          resetFormAndCloseModal();
          if (!result) return;

          const updatedOrder = serviceOrders.find((order) => order.id === result.id);
          
          updateServiceOrder(updatedOrder);
        } else {
          const result = await orderStore.createExtra({
            ...values,
            vat: tenantVat,
          });

          resetFormAndCloseModal();
          if (!result) return;

          const updatedOrder = serviceOrders.find((order) => order.id === result.serviceOrderId);
          if (updatedOrder) {
            updatedOrder.extras.push(result);
            updateServiceOrder(updatedOrder);
          }
        }
      },
      [orderStore, addForm, tenantVat, serviceOrders, selectServices, handleServiceOrdersUpdate]
    );

    const handleUpdate = useCallback(
      async (key: React.Key) => {
        tableForm.validateFields().then(async (values) => {
          const index = serviceOrderExtras.findIndex((x) => x.id === key);
          const serviceOrderExtra = serviceOrderExtras[index];
          const serviceOrderId = serviceOrderExtra.serviceOrderId;
          await orderStore.updateExtra({ ...values, id: key, serviceOrderId }).then(() => {
            const serviceOrder = serviceOrders.find((x) => x.id === serviceOrderId);

            if (serviceOrder) {
              handleServiceOrdersUpdate && handleServiceOrdersUpdate(serviceOrder);
            }
          });
          setEditingKey('');
        });
      },
      [tableForm, orderStore, serviceOrderExtras]
    );

    const handleDelete = useCallback(async () => {
      const { id, serviceOrderId } = deleteExtra;
      await orderStore.deleteExtra({ id }, serviceOrderId).then((result) => {
        setShowDeleteModal(false);

        if (!result) return;
        const serviceOrder = serviceOrders.find((x) => x.id === serviceOrderId);

        if (serviceOrder) {
          serviceOrder.extras = serviceOrder.extras.filter((x) => x.id !== id);
          handleServiceOrdersUpdate && handleServiceOrdersUpdate(serviceOrder);
        }
      });
    }, [orderStore, deleteExtra]);

    const handleDeleteClick = (deleteExtra: DeleteExtra) => {
      setDeleteExtra(deleteExtra);
      setShowDeleteModal(true);
    };

    const handleEdit = (record: Partial<ServiceOrderExtra> & { key: React.Key }) => {
      tableForm.setFieldsValue(record);
      setEditingKey(record.id || '');
    };

    const handleAdd = () => {
      addForm.setFieldsValue({
        ...InitialServiceOrderExtra,
        serviceOrderId: serviceOrders.length === 1 ? serviceOrders[0].id : undefined,
      });
      setShowAddModal(true);
    };

    const handleCancel = () => {
      setEditingKey('');
    };

    const handleCloseAddModal = () => {
      setShowAddModal(false);
      addForm.resetFields();
    };

    return (
      <Presentation
        tableForm={tableForm}
        addForm={addForm}
        showAddModal={showAddModal}
        showDeleteModal={showDeleteModal}
        disableServiceAdd={disableServiceAdd}
        hideAction={hideAction}
        serviceOrders={serviceOrders}
        dataSource={serviceOrderExtras}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        handleCreate={handleCreate}
        handleCancel={handleCancel}
        handleUpdate={handleUpdate}
        isEditing={isEditing}
        setShowAddModal={setShowAddModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={handleDelete}
        handleDeleteClick={handleDeleteClick}
        handleCloseAddModal={handleCloseAddModal}
      />
    );
  }
);
